import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise'

import { AgGridWidget, createContextMenu } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { Badge } from '@fortem-monorepo/react-ui'
import type { Strategies } from '@fortem-monorepo/web-sdk'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { useStrategies } from '@/react-query/queries/strategies'

import { useStrategyActions } from '../StrategyActions.context'
import { getStrategyStatusLabel } from '../strategy-labels'

const StrategyManagementCell = ({ value, node }: ICellRendererParams<Strategies.Strategy, boolean>) => {
  if (node.group) {
    return null
  }

  return <Badge theme={value ? 'success' : 'info'}>{value ? 'INTERNAL' : 'EXTERNAL'}</Badge>
}

const StrategyStatusCell = ({ value, node }: ICellRendererParams<Strategies.Strategy, boolean | null>) => {
  if (node.group) {
    return null
  }

  const label = getStrategyStatusLabel(value)
  if (value == null) {
    return label
  }

  return <Badge theme={value ? 'success' : 'destructive'}>{label}</Badge>
}

const columnDefs: ColDef<Strategies.Strategy>[] = [
  { field: 'strategy_type', headerName: 'Strategy type', rowGroup: true, hide: true },
  { field: 'strategy_id', headerName: 'ID', width: 100 },
  { field: 'strategy_name', headerName: 'Strategy name', flex: 1, minWidth: 200 },
  { field: 'currency', headerName: 'Currency' },
  {
    field: 'is_internal',
    headerName: 'Management',
    cellRenderer: StrategyManagementCell,
  },
  {
    field: 'active',
    headerName: 'Status',
    cellRenderer: StrategyStatusCell,
  },
]

export const StrategiesGrid = () => {
  const query = useStrategies()
  const actions = useStrategyActions()
  const { hasPermissions } = useUserContext()

  const canWrite = hasPermissions((permissions) => ({ permissions: [permissions.WRITE_ADMIN_FUNDS] }))

  const contextMenu = createContextMenu<Strategies.Strategy>((params) => {
    const defaults = params.defaultItems ?? []
    const row = params.node?.data
    if (!canWrite || params.node?.group || !row) {
      return defaults
    }

    return [
      { name: `Update ${row.strategy_name}`, disabled: !!actions.actionState, action: () => actions.openUpdate(row) },
      'separator',
      ...defaults,
    ]
  })

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <HeaderConfigBar className="shrink-0">
        <Button size="sm" disabled={!canWrite || !!actions.actionState} onClick={actions.openCreate}>
          Create strategy
        </Button>
      </HeaderConfigBar>
      {query.isError && (
        <div role="alert" className="text-destructive shrink-0">
          Unable to load strategies. {getErrorMessage(query.error)}
        </div>
      )}
      <AgGridWidget
        query={query}
        widget={{ exposed: true, className: 'h-full min-h-0 w-full flex-1' }}
        widgetHeader={{ title: 'Strategies', className: 'shrink-0' }}
        widgetContent={{ className: 'min-h-0 flex-1 overflow-hidden' }}
        grid={{
          rowData: query.data ?? [],
          columnDefs,
          domLayout: 'normal',
          getContextMenuItems: contextMenu,
          defaultColDef: { editable: false, cellStyle: { textAlign: 'center' }, headerClass: 'header-center' },
          autoGroupColumnDef: { headerName: 'Strategy type', minWidth: 200, cellStyle: { textAlign: 'center' } },
          getRowId: ({ data }: { data: Strategies.Strategy }) => String(data.strategy_id),
        }}
      />
    </div>
  )
}
